import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, getAuthHeader } from '@/context/AuthContext';
import axios from 'axios';
import { Plus, Pencil, Trash2, LogOut, Save, GripVertical, Eye, EyeOff, Upload, X, Settings, LayoutList, BarChart3, Monitor, Smartphone, Tablet, Globe, MousePointerClick, TrendingUp, Calendar, Gift, Star } from 'lucide-react';
import { Switch } from '@/components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AnalyticsTab() {
  const [summary, setSummary] = useState(null);
  const [clicks, setClicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterSite, setFilterSite] = useState('');
  const [filterDevice, setFilterDevice] = useState('');
  const [filterBrowser, setFilterBrowser] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const [summaryRes, clicksRes] = await Promise.all([
        axios.get(`${API}/analytics/summary?${params}`, { headers: getAuthHeader(), withCredentials: true }),
        axios.get(`${API}/analytics/clicks?${params}${filterSite ? `&site_id=${filterSite}` : ''}${filterDevice ? `&device_type=${filterDevice}` : ''}${filterBrowser ? `&browser=${filterBrowser}` : ''}`, { headers: getAuthHeader(), withCredentials: true })
      ]);
      setSummary(summaryRes.data);
      setClicks(clicksRes.data);
    } catch {}
    setLoading(false);
  }, [startDate, endDate, filterSite, filterDevice, filterBrowser]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setQuickDate = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setFilterSite('');
    setFilterDevice('');
    setFilterBrowser('');
  };

  const deviceIcon = (type) => {
    if (type === 'mobile') return <Smartphone size={14} className="text-yellow-400" />;
    if (type === 'tablet') return <Tablet size={14} className="text-blue-400" />;
    return <Monitor size={14} className="text-green-400" />;
  };

  const maxSiteClicks = summary?.by_site?.[0]?.clicks || 1;

  if (loading && !summary) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="glass-card rounded-xl p-4 text-center" data-testid="stat-total">
          <MousePointerClick size={20} className="text-yellow-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{summary?.total_clicks || 0}</p>
          <p className="text-xs text-zinc-500 mt-1">Toplam Tiklama</p>
        </div>
        <div className="glass-card rounded-xl p-4 text-center" data-testid="stat-sites">
          <Globe size={20} className="text-yellow-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{summary?.by_site?.length || 0}</p>
          <p className="text-xs text-zinc-500 mt-1">Aktif Site</p>
        </div>
        <div className="glass-card rounded-xl p-4 text-center" data-testid="stat-mobile">
          <Smartphone size={20} className="text-yellow-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{summary?.by_device?.find(d => d.name === 'mobile')?.clicks || 0}</p>
          <p className="text-xs text-zinc-500 mt-1">Mobil Tiklama</p>
        </div>
        <div className="glass-card rounded-xl p-4 text-center" data-testid="stat-desktop">
          <Monitor size={20} className="text-yellow-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{summary?.by_device?.find(d => d.name === 'desktop')?.clicks || 0}</p>
          <p className="text-xs text-zinc-500 mt-1">Desktop Tiklama</p>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card rounded-xl p-4 space-y-3" data-testid="analytics-filters">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-white flex items-center gap-2"><Calendar size={14} className="text-yellow-400" /> Filtreler</p>
          <button onClick={clearFilters} className="text-xs text-zinc-500 hover:text-yellow-400 transition-colors">Temizle</button>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setQuickDate(1)} className="text-xs px-3 py-1.5 rounded-full bg-zinc-900 text-zinc-400 hover:text-yellow-400 hover:bg-zinc-800 transition-colors" data-testid="filter-today">Bugun</button>
          <button onClick={() => setQuickDate(7)} className="text-xs px-3 py-1.5 rounded-full bg-zinc-900 text-zinc-400 hover:text-yellow-400 hover:bg-zinc-800 transition-colors" data-testid="filter-7d">Son 7 Gun</button>
          <button onClick={() => setQuickDate(30)} className="text-xs px-3 py-1.5 rounded-full bg-zinc-900 text-zinc-400 hover:text-yellow-400 hover:bg-zinc-800 transition-colors" data-testid="filter-30d">Son 30 Gun</button>
          <button onClick={() => setQuickDate(90)} className="text-xs px-3 py-1.5 rounded-full bg-zinc-900 text-zinc-400 hover:text-yellow-400 hover:bg-zinc-800 transition-colors" data-testid="filter-90d">Son 90 Gun</button>
          <button onClick={clearFilters} className="text-xs px-3 py-1.5 rounded-full bg-zinc-900 text-zinc-400 hover:text-yellow-400 hover:bg-zinc-800 transition-colors" data-testid="filter-all">Tum Zamanlar</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="admin-input text-xs" data-testid="filter-start-date" />
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="admin-input text-xs" data-testid="filter-end-date" />
          <select value={filterDevice} onChange={e => setFilterDevice(e.target.value)} className="admin-input text-xs" data-testid="filter-device">
            <option value="">Tum Cihazlar</option>
            <option value="mobile">Mobil</option>
            <option value="desktop">Desktop</option>
            <option value="tablet">Tablet</option>
          </select>
          <select value={filterBrowser} onChange={e => setFilterBrowser(e.target.value)} className="admin-input text-xs" data-testid="filter-browser">
            <option value="">Tum Tarayicilar</option>
            <option value="Chrome">Chrome</option>
            <option value="Safari">Safari</option>
            <option value="Firefox">Firefox</option>
            <option value="Edge">Edge</option>
            <option value="Opera">Opera</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Per Site Clicks */}
        <div className="glass-card rounded-xl p-5" data-testid="chart-by-site">
          <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <TrendingUp size={16} className="text-yellow-400" /> Sitelere Gore Tiklamalar
          </h3>
          <div className="space-y-3">
            {summary?.by_site?.map((s, i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-zinc-300 font-medium">{s.name}</span>
                  <span className="text-xs text-yellow-400 font-bold">{s.clicks}</span>
                </div>
                <div className="w-full h-2 bg-zinc-900 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full transition-all duration-500" style={{ width: `${(s.clicks / maxSiteClicks) * 100}%` }} />
                </div>
              </div>
            ))}
            {(!summary?.by_site || summary.by_site.length === 0) && (
              <p className="text-zinc-600 text-xs text-center py-4">Henuz tiklama verisi yok</p>
            )}
          </div>
        </div>

        {/* Device & Browser */}
        <div className="space-y-4">
          {/* Device Breakdown */}
          <div className="glass-card rounded-xl p-5" data-testid="chart-by-device">
            <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Smartphone size={16} className="text-yellow-400" /> Cihaz Dagilimi
            </h3>
            <div className="space-y-2">
              {summary?.by_device?.map((d, i) => (
                <div key={i} className="flex items-center justify-between bg-zinc-900/50 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    {deviceIcon(d.name)}
                    <span className="text-xs text-zinc-300 capitalize">{d.name === 'mobile' ? 'Mobil' : d.name === 'desktop' ? 'Desktop' : 'Tablet'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-yellow-400 font-bold">{d.clicks}</span>
                    <span className="text-[10px] text-zinc-600">({summary.total_clicks ? Math.round((d.clicks / summary.total_clicks) * 100) : 0}%)</span>
                  </div>
                </div>
              ))}
              {(!summary?.by_device || summary.by_device.length === 0) && (
                <p className="text-zinc-600 text-xs text-center py-2">Veri yok</p>
              )}
            </div>
          </div>

          {/* Browser Breakdown */}
          <div className="glass-card rounded-xl p-5" data-testid="chart-by-browser">
            <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Globe size={16} className="text-yellow-400" /> Tarayici Dagilimi
            </h3>
            <div className="space-y-2">
              {summary?.by_browser?.map((b, i) => (
                <div key={i} className="flex items-center justify-between bg-zinc-900/50 rounded-lg px-3 py-2">
                  <span className="text-xs text-zinc-300">{b.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-yellow-400 font-bold">{b.clicks}</span>
                    <span className="text-[10px] text-zinc-600">({summary.total_clicks ? Math.round((b.clicks / summary.total_clicks) * 100) : 0}%)</span>
                  </div>
                </div>
              ))}
              {(!summary?.by_browser || summary.by_browser.length === 0) && (
                <p className="text-zinc-600 text-xs text-center py-2">Veri yok</p>
              )}
            </div>
          </div>

          {/* OS Breakdown */}
          <div className="glass-card rounded-xl p-5" data-testid="chart-by-os">
            <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Monitor size={16} className="text-yellow-400" /> Isletim Sistemi
            </h3>
            <div className="space-y-2">
              {summary?.by_os?.map((o, i) => (
                <div key={i} className="flex items-center justify-between bg-zinc-900/50 rounded-lg px-3 py-2">
                  <span className="text-xs text-zinc-300">{o.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-yellow-400 font-bold">{o.clicks}</span>
                    <span className="text-[10px] text-zinc-600">({summary.total_clicks ? Math.round((o.clicks / summary.total_clicks) * 100) : 0}%)</span>
                  </div>
                </div>
              ))}
              {(!summary?.by_os || summary.by_os.length === 0) && (
                <p className="text-zinc-600 text-xs text-center py-2">Veri yok</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Daily Chart */}
      {summary?.by_date?.length > 0 && (
        <div className="glass-card rounded-xl p-5" data-testid="chart-by-date">
          <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <BarChart3 size={16} className="text-yellow-400" /> Gunluk Tiklamalar
          </h3>
          <div className="flex items-end gap-1 h-32 overflow-x-auto pb-6 relative">
            {summary.by_date.map((d, i) => {
              const maxDaily = Math.max(...summary.by_date.map(x => x.clicks));
              const h = maxDaily > 0 ? (d.clicks / maxDaily) * 100 : 0;
              return (
                <div key={i} className="flex flex-col items-center gap-1 min-w-[28px] relative group">
                  <span className="text-[9px] text-yellow-400 font-bold opacity-0 group-hover:opacity-100 transition-opacity">{d.clicks}</span>
                  <div className="w-5 bg-gradient-to-t from-yellow-600 to-yellow-400 rounded-t transition-all duration-300 hover:from-yellow-500 hover:to-yellow-300" style={{ height: `${Math.max(h, 4)}%` }} />
                  <span className="text-[8px] text-zinc-700 absolute -bottom-5 whitespace-nowrap">{d.date.slice(5)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Clicks Table */}
      <div className="glass-card rounded-xl p-5" data-testid="recent-clicks-table">
        <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
          <MousePointerClick size={16} className="text-yellow-400" /> Son Tiklamalar
          <span className="text-[10px] text-zinc-600 font-normal ml-auto">{clicks.length} kayit</span>
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left py-2 px-2 text-zinc-500 font-medium">Site</th>
                <th className="text-left py-2 px-2 text-zinc-500 font-medium">Cihaz</th>
                <th className="text-left py-2 px-2 text-zinc-500 font-medium">Tarayici</th>
                <th className="text-left py-2 px-2 text-zinc-500 font-medium">IS</th>
                <th className="text-left py-2 px-2 text-zinc-500 font-medium">Tarih</th>
              </tr>
            </thead>
            <tbody>
              {clicks.slice(0, 50).map((c, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="py-2 px-2 text-zinc-300 font-medium">{c.site_name}</td>
                  <td className="py-2 px-2">
                    <span className="flex items-center gap-1">
                      {deviceIcon(c.device_type)}
                      <span className="text-zinc-400 capitalize">{c.device_type === 'mobile' ? 'Mobil' : c.device_type === 'desktop' ? 'Desktop' : 'Tablet'}</span>
                    </span>
                  </td>
                  <td className="py-2 px-2 text-zinc-400">{c.browser}</td>
                  <td className="py-2 px-2 text-zinc-400">{c.os}</td>
                  <td className="py-2 px-2 text-zinc-500">{new Date(c.timestamp).toLocaleString('tr-TR')}</td>
                </tr>
              ))}
              {clicks.length === 0 && (
                <tr><td colSpan="5" className="text-center py-8 text-zinc-600">Henuz tiklama verisi yok</td></tr>
              )}
            </tbody>
          </table>
          {clicks.length > 50 && <p className="text-[10px] text-zinc-700 mt-2 text-center">Son 50 kayit gosteriliyor (toplam {clicks.length})</p>}
        </div>
      </div>
    </div>
  );
}

function CaseAnalyticsTab() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const { data } = await axios.get(`${API}/analytics/case-summary?${params}`, { headers: getAuthHeader(), withCredentials: true });
      setSummary(data);
    } catch {}
    setLoading(false);
  }, [startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setQuickDate = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const clearFilters = () => { setStartDate(''); setEndDate(''); };
  const maxSiteSpins = summary?.by_site?.[0]?.spins || 1;

  if (loading && !summary) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="glass-card rounded-xl p-4 text-center" data-testid="case-stat-total">
          <Gift size={20} className="text-yellow-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{summary?.total_spins || 0}</p>
          <p className="text-xs text-zinc-500 mt-1">Toplam Çark Çevirme</p>
        </div>
        <div className="glass-card rounded-xl p-4 text-center">
          <Globe size={20} className="text-yellow-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{summary?.by_site?.length || 0}</p>
          <p className="text-xs text-zinc-500 mt-1">Gelen Site Sayısı</p>
        </div>
        <div className="glass-card rounded-xl p-4 text-center">
          <Smartphone size={20} className="text-yellow-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{summary?.by_device?.find(d => d.name === 'mobile')?.spins || 0}</p>
          <p className="text-xs text-zinc-500 mt-1">Mobil Çevirme</p>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-white flex items-center gap-2"><Calendar size={14} className="text-yellow-400" /> Tarih Aralığı</p>
          <button onClick={clearFilters} className="text-xs text-zinc-500 hover:text-yellow-400 transition-colors">Temizle</button>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setQuickDate(1)} className="text-xs px-3 py-1.5 rounded-full bg-zinc-900 text-zinc-400 hover:text-yellow-400 hover:bg-zinc-800 transition-colors">Bugün</button>
          <button onClick={() => setQuickDate(7)} className="text-xs px-3 py-1.5 rounded-full bg-zinc-900 text-zinc-400 hover:text-yellow-400 hover:bg-zinc-800 transition-colors">Son 7 Gün</button>
          <button onClick={() => setQuickDate(30)} className="text-xs px-3 py-1.5 rounded-full bg-zinc-900 text-zinc-400 hover:text-yellow-400 hover:bg-zinc-800 transition-colors">Son 30 Gün</button>
          <button onClick={() => setQuickDate(90)} className="text-xs px-3 py-1.5 rounded-full bg-zinc-900 text-zinc-400 hover:text-yellow-400 hover:bg-zinc-800 transition-colors">Son 90 Gün</button>
          <button onClick={clearFilters} className="text-xs px-3 py-1.5 rounded-full bg-zinc-900 text-zinc-400 hover:text-yellow-400 hover:bg-zinc-800 transition-colors">Tüm Zamanlar</button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="admin-input text-xs" />
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="admin-input text-xs" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Site breakdown */}
        <div className="glass-card rounded-xl p-5">
          <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <TrendingUp size={16} className="text-yellow-400" /> Sitelere Göre Çevirme
          </h3>
          <div className="space-y-3">
            {summary?.by_site?.map((s, i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-zinc-300 font-medium">{s.name}</span>
                  <span className="text-xs text-yellow-400 font-bold">{s.spins}</span>
                </div>
                <div className="w-full h-2 bg-zinc-900 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full transition-all duration-500" style={{ width: `${(s.spins / maxSiteSpins) * 100}%` }} />
                </div>
              </div>
            ))}
            {(!summary?.by_site || summary.by_site.length === 0) && <p className="text-zinc-600 text-xs text-center py-4">Henüz çark verisi yok</p>}
          </div>
        </div>

        {/* Bonus & Device breakdown */}
        <div className="space-y-4">
          <div className="glass-card rounded-xl p-5">
            <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Star size={16} className="text-yellow-400" /> Bonus Dağılımı
            </h3>
            <div className="space-y-2">
              {summary?.by_bonus?.map((b, i) => (
                <div key={i} className="flex items-center justify-between bg-zinc-900/50 rounded-lg px-3 py-2">
                  <span className="text-xs text-zinc-300">{b.name}</span>
                  <span className="text-xs text-yellow-400 font-bold">{b.spins}x</span>
                </div>
              ))}
              {(!summary?.by_bonus || summary.by_bonus.length === 0) && <p className="text-zinc-600 text-xs text-center py-2">Veri yok</p>}
            </div>
          </div>

          <div className="glass-card rounded-xl p-5">
            <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Smartphone size={16} className="text-yellow-400" /> Cihaz Dağılımı
            </h3>
            <div className="space-y-2">
              {summary?.by_device?.map((d, i) => (
                <div key={i} className="flex items-center justify-between bg-zinc-900/50 rounded-lg px-3 py-2">
                  <span className="text-xs text-zinc-300 capitalize">{d.name === 'mobile' ? 'Mobil' : d.name === 'desktop' ? 'Desktop' : 'Tablet'}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-yellow-400 font-bold">{d.spins}</span>
                    <span className="text-[10px] text-zinc-600">({summary.total_spins ? Math.round((d.spins / summary.total_spins) * 100) : 0}%)</span>
                  </div>
                </div>
              ))}
              {(!summary?.by_device || summary.by_device.length === 0) && <p className="text-zinc-600 text-xs text-center py-2">Veri yok</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Daily Chart */}
      {summary?.by_date?.length > 0 && (
        <div className="glass-card rounded-xl p-5">
          <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <BarChart3 size={16} className="text-yellow-400" /> Günlük Çevirme
          </h3>
          <div className="flex items-end gap-1 h-32 overflow-x-auto pb-6 relative">
            {summary.by_date.map((d, i) => {
              const maxDaily = Math.max(...summary.by_date.map(x => x.spins));
              const h = maxDaily > 0 ? (d.spins / maxDaily) * 100 : 0;
              return (
                <div key={i} className="flex flex-col items-center gap-1 min-w-[28px] relative group">
                  <span className="text-[9px] text-yellow-400 font-bold opacity-0 group-hover:opacity-100 transition-opacity">{d.spins}</span>
                  <div className="w-5 bg-gradient-to-t from-yellow-600 to-yellow-400 rounded-t transition-all duration-300 hover:from-yellow-500 hover:to-yellow-300" style={{ height: `${Math.max(h, 4)}%` }} />
                  <span className="text-[8px] text-zinc-700 absolute -bottom-5 whitespace-nowrap">{d.date.slice(5)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('sites');
  const [sites, setSites] = useState([]);
  const [seo, setSeo] = useState({ meta_title: '', meta_description: '', page_content: {} });
  const [editSite, setEditSite] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchSites = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sites`, { headers: getAuthHeader(), withCredentials: true });
      setSites(data);
    } catch {}
  }, []);

  const fetchSeo = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/seo`, { headers: getAuthHeader(), withCredentials: true });
      setSeo(data);
    } catch {}
  }, []);

  useEffect(() => { fetchSites(); fetchSeo(); }, [fetchSites, fetchSeo]);

  const handleLogout = async () => { await logout(); navigate('/admin/login'); };

  const emptyForm = { name: '', logo_url: '', bonus_title: '', bonus_description: '', affiliate_link: '', button_text: 'Kayit Ol', sort_order: sites.length + 1, is_active: true };

  const openNew = () => { setEditSite(emptyForm); setShowForm(true); };
  const openEdit = (site) => { setEditSite({ ...site }); setShowForm(true); };
  const closeForm = () => { setEditSite(null); setShowForm(false); };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/upload/logo`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }, withCredentials: true });
      setEditSite(prev => ({ ...prev, logo_url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.storage_path}` }));
    } catch {}
    setUploading(false);
  };

  const saveSite = async () => {
    if (!editSite) return;
    setSaving(true);
    try {
      const payload = { name: editSite.name, logo_url: editSite.logo_url || '', bonus_title: editSite.bonus_title, bonus_description: editSite.bonus_description || '', affiliate_link: editSite.affiliate_link, button_text: editSite.button_text || 'Kayit Ol', sort_order: editSite.sort_order || 0, is_active: editSite.is_active !== false };
      if (editSite.id) {
        await axios.put(`${API}/sites/${editSite.id}`, payload, { headers: getAuthHeader(), withCredentials: true });
      } else {
        await axios.post(`${API}/sites`, payload, { headers: getAuthHeader(), withCredentials: true });
      }
      closeForm();
      fetchSites();
    } catch {}
    setSaving(false);
  };

  const deleteSite = async (id) => {
    if (!window.confirm('Bu siteyi silmek istediginize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/sites/${id}`, { headers: getAuthHeader(), withCredentials: true });
      fetchSites();
    } catch {}
  };

  const saveSeo = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/seo`, seo, { headers: getAuthHeader(), withCredentials: true });
    } catch {}
    setSaving(false);
  };

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      {/* Admin Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>
              <span className="text-yellow-400">Bonusal</span>.vip
            </h1>
            <span className="text-xs text-zinc-600 bg-zinc-900 rounded-full px-2 py-0.5">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-xs text-zinc-500 hover:text-yellow-400 transition-colors flex items-center gap-1" data-testid="view-site-link">
              <Eye size={14} /> Siteyi Gor
            </a>
            <button onClick={handleLogout} className="text-zinc-500 hover:text-red-400 transition-colors flex items-center gap-1 text-xs" data-testid="admin-logout-btn">
              <LogOut size={14} /> Cikis
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto" data-testid="admin-tabs">
          <button onClick={() => setTab('sites')} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${tab === 'sites' ? 'bg-yellow-400 text-black' : 'bg-zinc-900 text-zinc-400 hover:text-white'}`} data-testid="tab-sites">
            <LayoutList size={16} /> Siteler
          </button>
          <button onClick={() => setTab('analytics')} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${tab === 'analytics' ? 'bg-yellow-400 text-black' : 'bg-zinc-900 text-zinc-400 hover:text-white'}`} data-testid="tab-analytics">
            <BarChart3 size={16} /> Analitik
          </button>
          <button onClick={() => setTab('case')} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${tab === 'case' ? 'bg-yellow-400 text-black' : 'bg-zinc-900 text-zinc-400 hover:text-white'}`} data-testid="tab-case">
            <Gift size={16} /> Çark
          </button>
          <button onClick={() => setTab('seo')} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${tab === 'seo' ? 'bg-yellow-400 text-black' : 'bg-zinc-900 text-zinc-400 hover:text-white'}`} data-testid="tab-seo">
            <Settings size={16} /> SEO Ayarlari
          </button>
        </div>

        {/* Sites Tab */}
        {tab === 'sites' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Bonus Siteleri</h2>
              <button onClick={openNew} className="btn-glow text-sm flex items-center gap-2" data-testid="add-site-btn">
                <Plus size={16} /> Yeni Site Ekle
              </button>
            </div>

            <div className="space-y-3">
              {sites.map((site, idx) => (
                <div key={site.id} className="glass-card rounded-xl p-4 flex items-center gap-4" data-testid={`site-row-${idx}`}>
                  <GripVertical size={16} className="text-zinc-700 shrink-0" />
                  <div className="w-10 h-10 rounded-lg bg-zinc-900 border border-yellow-400/20 flex items-center justify-center shrink-0 overflow-hidden">
                    {site.logo_url ? (
                      <img src={site.logo_url} alt={site.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-yellow-400 font-bold text-sm">{site.name?.charAt(0)}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium text-sm truncate">{site.name}</p>
                      {site.is_active ? (
                        <span className="text-[10px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded-full">Aktif</span>
                      ) : (
                        <span className="text-[10px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded-full">Pasif</span>
                      )}
                    </div>
                    <p className="text-zinc-500 text-xs truncate">{site.bonus_title}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => openEdit(site)} className="p-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-yellow-400 transition-colors" data-testid={`edit-site-${idx}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => deleteSite(site.id)} className="p-2 rounded-lg bg-zinc-900 hover:bg-red-900/30 text-zinc-400 hover:text-red-400 transition-colors" data-testid={`delete-site-${idx}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
              {sites.length === 0 && <p className="text-zinc-600 text-center py-12">Henuz site eklenmemis.</p>}
            </div>

            {/* Edit/Add Modal */}
            {showForm && editSite && (
              <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={closeForm}>
                <div className="bg-zinc-950 border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="site-form-modal">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>{editSite.id ? 'Siteyi Duzenle' : 'Yeni Site Ekle'}</h3>
                    <button onClick={closeForm} className="text-zinc-500 hover:text-white"><X size={20} /></button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-zinc-400 text-xs font-medium mb-1 block">Site Adi</label>
                      <input className="admin-input" value={editSite.name} onChange={e => setEditSite(p => ({ ...p, name: e.target.value }))} placeholder="Site adi" data-testid="site-name-input" />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs font-medium mb-1 block">Logo</label>
                      <div className="flex gap-2 items-center">
                        <input className="admin-input flex-1" value={editSite.logo_url} onChange={e => setEditSite(p => ({ ...p, logo_url: e.target.value }))} placeholder="Logo URL veya yukleyin" data-testid="site-logo-input" />
                        <label className={`shrink-0 p-2.5 rounded-lg bg-zinc-900 border border-white/10 cursor-pointer hover:border-yellow-400/30 transition-colors ${uploading ? 'opacity-50' : ''}`}>
                          <Upload size={16} className="text-zinc-400" />
                          <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={uploading} data-testid="logo-upload-input" />
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs font-medium mb-1 block">Bonus Basligi</label>
                      <input className="admin-input" value={editSite.bonus_title} onChange={e => setEditSite(p => ({ ...p, bonus_title: e.target.value }))} placeholder="1000 TL Deneme Bonusu" data-testid="site-bonus-title-input" />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs font-medium mb-1 block">Bonus Aciklamasi</label>
                      <textarea className="admin-input min-h-[80px] resize-y" value={editSite.bonus_description} onChange={e => setEditSite(p => ({ ...p, bonus_description: e.target.value }))} placeholder="Bonus detaylari..." data-testid="site-bonus-desc-input" />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs font-medium mb-1 block">Affiliate Link</label>
                      <input className="admin-input" value={editSite.affiliate_link} onChange={e => setEditSite(p => ({ ...p, affiliate_link: e.target.value }))} placeholder="https://..." data-testid="site-affiliate-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-zinc-400 text-xs font-medium mb-1 block">Buton Metni</label>
                        <input className="admin-input" value={editSite.button_text} onChange={e => setEditSite(p => ({ ...p, button_text: e.target.value }))} placeholder="Kayit Ol" data-testid="site-button-text-input" />
                      </div>
                      <div>
                        <label className="text-zinc-400 text-xs font-medium mb-1 block">Siralama</label>
                        <input type="number" className="admin-input" value={editSite.sort_order} onChange={e => setEditSite(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} data-testid="site-sort-input" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between bg-zinc-900/50 rounded-xl p-3">
                      <span className="text-sm text-zinc-400">Aktif Durum</span>
                      <Switch
                        checked={editSite.is_active}
                        onCheckedChange={(checked) => setEditSite(p => ({ ...p, is_active: checked }))}
                        className="data-[state=checked]:bg-yellow-400"
                        data-testid="site-active-switch"
                      />
                    </div>
                    <button onClick={saveSite} disabled={saving} className="btn-glow w-full flex items-center justify-center gap-2" data-testid="save-site-btn">
                      <Save size={16} /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {tab === 'analytics' && <AnalyticsTab />}

        {/* Case/Wheel Tab */}
        {tab === 'case' && <CaseAnalyticsTab />}

        {/* SEO Tab */}
        {tab === 'seo' && (
          <div className="max-w-2xl">
            <h2 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>SEO Ayarlari</h2>
            <div className="space-y-5">
              <div>
                <label className="text-zinc-400 text-xs font-medium mb-1 block">Meta Baslik (Title)</label>
                <input className="admin-input" value={seo.meta_title} onChange={e => setSeo(p => ({ ...p, meta_title: e.target.value }))} placeholder="Sayfa basligi" data-testid="seo-title-input" />
              </div>
              <div>
                <label className="text-zinc-400 text-xs font-medium mb-1 block">Meta Aciklama (Description)</label>
                <textarea className="admin-input min-h-[100px] resize-y" value={seo.meta_description} onChange={e => setSeo(p => ({ ...p, meta_description: e.target.value }))} placeholder="Sayfa aciklamasi" data-testid="seo-desc-input" />
              </div>
              <button onClick={saveSeo} disabled={saving} className="btn-glow flex items-center gap-2" data-testid="save-seo-btn">
                <Save size={16} /> {saving ? 'Kaydediliyor...' : 'SEO Ayarlarini Kaydet'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
